import React, { useEffect, useState } from "react";
import { 
  Button, 
  Card, 
  CardBody, 
  CardHeader, 
  Chip, 
  Select, 
  SelectItem,
  Progress,
  Badge,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Textarea,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow
} from "@nextui-org/react";
import { useNavigate } from "react-router-dom";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import {
  getDataWithFilterUrl,
  StringLimit,
} from "../../controllers/APIController";
import { 
  DocumentChartBarIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  SparklesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChartBarIcon
} from "@heroicons/react/24/outline";
import CardSkeletonContainer from "../../components/Skeleten/CardSkeletonContainer";
import { DateFormator } from "../../utils/helper";
import { StandardCancelButton } from "../../components/StandardButtons";
import { useQuery } from "@tanstack/react-query";

// Available colors for report cards
const reportCardColors = ["secondary", "success", "warning", "danger", "default"];

// Mock standards data - replace with real API call
const mockStandards = [
  { id: 1, name: "ISO 14001", version: "2015", status: "mapped", coverage: 85, category: "Environmental", lastUpdated: "2024-01-15" },
  { id: 2, name: "ISO 9001", version: "2015", status: "mapped", coverage: 92, category: "Quality", lastUpdated: "2024-01-10" },
  { id: 3, name: "GRI Standards", version: "2021", status: "mapped", coverage: 78, category: "Sustainability", lastUpdated: "2024-01-12" },
  { id: 4, name: "SASB Standards", version: "2023", status: "unmapped", coverage: 0, category: "Financial", lastUpdated: "2024-01-05" },
  { id: 5, name: "TCFD", version: "2017", status: "mapped", coverage: 65, category: "Climate", lastUpdated: "2024-01-08" },
  { id: 6, name: "CDP", version: "2024", status: "in-progress", coverage: 45, category: "Environmental", lastUpdated: "2024-01-20" }
];

// Assessment years
const assessmentYears = [
  { key: "2024", label: "2024" },
  { key: "2023", label: "2023" },
  { key: "2022", label: "2022" },
  { key: "2021", label: "2021" }
];

/**
 * Standards Breakdown Component
 * Displays detailed standards breakdown statistics
 */
const StandardsBreakdown = () => {
  const mappedCount = mockStandards.filter(s => s.status === 'mapped').length;
  const inProgressCount = mockStandards.filter(s => s.status === 'in-progress').length;
  const unmappedCount = mockStandards.filter(s => s.status === 'unmapped').length;
  
  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="flex items-center justify-between font-medium text-base">
        <span>Total Standards</span>
        <span className="text-2xl font-bold text-emerald-600">{mockStandards.length}</span>
      </div>
      <Divider />
      
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-slate-600">Mapped</span>
          <span className="font-medium text-emerald-600">{mappedCount}</span>
        </div>
        <Divider />
        
        <div className="flex items-center justify-between">
          <span className="text-slate-600">In Progress</span>
          <span className="font-medium text-amber-600">{inProgressCount}</span>
        </div>
        <Divider />
        
        <div className="flex items-center justify-between">
          <span className="text-slate-600">Unmapped</span>
          <span className="font-medium text-gray-600">{unmappedCount}</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Standards Table Component
 * Renders a table showing standards and their details
 */
const StandardsTable = () => {
  return (
    <Table
      removeWrapper
      aria-label="Standards mapping table"
      isStriped={true}
      classNames={{
        th: "bg-slate-50 text-slate-700 font-semibold text-xs border-b border-slate-100 py-3",
        td: "py-3 border-b border-slate-100 text-sm"
      }}
    >
      <TableHeader>
        <TableColumn>Standard</TableColumn>
        <TableColumn>Category</TableColumn>
        <TableColumn>Status</TableColumn>
        <TableColumn>Coverage</TableColumn>
        <TableColumn>Last Updated</TableColumn>
      </TableHeader>
      <TableBody>
        {mockStandards.slice(0, 5).map((standard) => (
          <TableRow
            key={standard.id}
            className="cursor-pointer hover:bg-slate-50"
          >
            <TableCell className="font-medium text-slate-700">
              <div>
                <div className="font-semibold">{standard.name}</div>
                <div className="text-xs text-slate-500">v{standard.version}</div>
              </div>
            </TableCell>
            <TableCell className="text-slate-600">
              {standard.category}
            </TableCell>
            <TableCell>
              <Chip
                size="sm"
                color={standard.status === 'mapped' ? 'success' : standard.status === 'in-progress' ? 'warning' : 'danger'}
                variant="flat"
                startContent={
                  standard.status === 'mapped' ? <CheckCircleIcon className="w-3 h-3" /> :
                  standard.status === 'in-progress' ? <ClockIcon className="w-3 h-3" /> :
                  <ExclamationTriangleIcon className="w-3 h-3" />
                }
              >
                {standard.status.charAt(0).toUpperCase() + standard.status.slice(1)}
              </Chip>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{standard.coverage}%</span>
                <Progress
                  size="sm"
                  value={standard.coverage}
                  color={standard.status === 'mapped' ? 'success' : standard.status === 'in-progress' ? 'warning' : 'danger'}
                  className="w-16"
                />
              </div>
            </TableCell>
            <TableCell className="text-slate-600 text-xs">
              {DateFormator(standard.lastUpdated)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/**
 * Reports Table Component
 * Renders a table showing reports and their details
 */
const ReportsTable = ({ reports }) => {
  return (
    <Table
      removeWrapper
      aria-label="Reports table"
      isStriped={true}
      classNames={{
        th: "bg-slate-50 text-slate-700 font-semibold text-xs border-b border-slate-100 py-3",
        td: "py-3 border-b border-slate-100 text-sm"
      }}
    >
      <TableHeader>
        <TableColumn>Report Name</TableColumn>
        <TableColumn>Status</TableColumn>
        <TableColumn>Created</TableColumn>
        <TableColumn>Actions</TableColumn>
      </TableHeader>
      <TableBody>
        {reports.slice(0, 5).map((report, index) => {
          const colorIndex = index % reportCardColors.length;
          const color = reportCardColors[colorIndex];
          
          return (
            <TableRow
              key={report?.id || index}
              className="cursor-pointer hover:bg-slate-50"
            >
              <TableCell className="font-medium text-slate-700">
                <div className="flex items-center gap-2">
                  <DocumentChartBarIcon className={`w-5 h-5 ${color === 'primary' ? 'text-emerald-600' : `text-${color}`}`} />
                  <span>{StringLimit(report?.attributes?.name, 30)}</span>
                </div>
              </TableCell>
              <TableCell>
                <Chip
                  size="sm"
                  color={report?.status === 'completed' ? 'success' : report?.status === 'in-progress' ? 'warning' : 'default'}
                  variant="flat"
                >
                  {report?.status || 'Draft'}
                </Chip>
              </TableCell>
              <TableCell className="text-slate-600 text-xs">
                {DateFormator(report?.attributes?.createdAt)}
              </TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    color="primary"
                    variant="flat"
                    className="text-xs px-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Navigate to edit
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    color="secondary"
                    variant="flat"
                    className="text-xs px-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Download functionality
                    }}
                  >
                    Download
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

/**
 * AI Report Generation Modal
 * Allows users to generate reports using AI
 */
const AIReportGenerationModal = ({ isOpen, onOpenChange }) => {
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [reportName, setReportName] = useState("");
  const [description, setDescription] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    setIsGenerating(true);
    // Simulate AI generation
    setTimeout(() => {
      setIsGenerating(false);
      onOpenChange(false);
      // Navigate to generated report or show success message
    }, 3000);
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="2xl">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <SparklesIcon className="w-5 h-5 text-emerald-600" />
            <span>Generate AI Report</span>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <Input
              label="Report Name"
              placeholder="Enter report name"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              size="sm"
            />
            <Textarea
              label="Description"
              placeholder="Enter report description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              size="sm"
            />
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Select Standards to Include
              </label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {mockStandards.map((standard) => (
                  <Chip
                    key={standard.id}
                    variant={selectedStandards.includes(standard.id) ? "solid" : "bordered"}
                    color={selectedStandards.includes(standard.id) ? "primary" : "default"}
                    className="cursor-pointer"
                    onClick={() => {
                      if (selectedStandards.includes(standard.id)) {
                        setSelectedStandards(selectedStandards.filter(id => id !== standard.id));
                      } else {
                        setSelectedStandards([...selectedStandards, standard.id]);
                      }
                    }}
                  >
                    {standard.name}
                  </Chip>
                ))}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <StandardCancelButton onPress={() => onOpenChange(false)} size="sm">
            Cancel
          </StandardCancelButton>
          <Button 
            color="primary" 
            onPress={handleGenerate}
            isLoading={isGenerating}
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isGenerating ? "Generating..." : "Generate Report"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

/**
 * Enhanced Report Component
 * Main reports page redesigned to match Scope1 page style
 */
const Report = () => {
  // Component state
  const [reports, setReports] = useState([]);
  const [selectedYear, setSelectedYear] = useState("2024");
  
  // Modal state
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  
  // Navigation hook
  const navigate = useNavigate();

  /**
   * Fetch reports data on component mount
   */
  const { data: ReportData, isLoading } = useQuery({
    queryKey: ["reports"],
    queryFn: () => getDataWithFilterUrl("reports"),
  });
  useEffect(() => {
    setReports(ReportData?.data || []);
  }, [ReportData]);

  // Calculate overall mapping progress
  const overallProgress = mockStandards.reduce((acc, standard) => acc + standard.coverage, 0) / mockStandards.length;

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Page Header */}
      <div className="bg-white border-b border-neutral-200 px-8 py-4 shadow-sm">
        <div className="max-w-full">
          <h1 className="text-xl font-semibold text-neutral-900">ESG Report Management</h1>
          <p className="text-sm text-neutral-600 mt-1">Manage standards mapping and generate comprehensive ESG reports</p>
        </div>
      </div>

      <BreadCrumbsPage>
        <div className="max-w-7xl mx-auto px-8 py-4">
          {/* Overview Card */}
          <div className="bg-white rounded-lg border border-neutral-200 shadow-sm p-6 mb-8">
            <h2 className="text-lg font-semibold text-neutral-900 mb-3">Report Management Overview</h2>
            <p className="text-neutral-600 leading-relaxed">
              ESG Report Management provides a comprehensive platform for managing sustainability standards mapping, 
              generating AI-powered reports, and tracking compliance across various frameworks. This system enables 
              organizations to streamline their ESG reporting process, ensure regulatory compliance, and demonstrate 
              their commitment to sustainable business practices.
            </p>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-3 mb-8">
            <Button
              color="primary"
              size="sm"
              startContent={<SparklesIcon className="w-4 h-4" />}
              onClick={onOpen}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Generate AI Report
            </Button>
            <Button
              color="secondary"
              size="sm"
              startContent={<PlusIcon className="w-4 h-4" />}
              onClick={() => navigate("create")}
            >
              Create Manual Report
            </Button>
            <Button
              color="default"
              size="sm"
              startContent={<DocumentTextIcon className="w-4 h-4" />}
              variant="bordered"
            >
              Import Report
            </Button>
            
            {/* Assessment Year Selector */}
            <Select
              label="Assessment Year"
              selectedKeys={[selectedYear]}
              onSelectionChange={(keys) => setSelectedYear(Array.from(keys)[0])}
              size="sm"
              className="w-40 ml-auto"
            >
              {assessmentYears.map((year) => (
                <SelectItem key={year.key} value={year.key}>
                  {year.label}
                </SelectItem>
              ))}
            </Select>
          </div>

          {/* Main Analytics Grid */}
          <div className="flex items-stretch min-h-[80vh] gap-6">
            {/* Left Sidebar - Standards Overview */}
            <div className="flex w-[25%]">
              <Card className="w-full px-4 py-6" shadow="md">
                <div className="text-center mb-6">
                  <p className="font-semibold text-slate-700 mb-2">Standards Mapping Progress</p>
                  <p className="text-4xl font-bold text-emerald-600 mb-1">{Math.round(overallProgress)}%</p>
                  <p className="text-sm text-slate-500">overall coverage</p>
                </div>

                <Divider className="my-6" />

                <div className="space-y-4">
                  <p className="font-semibold text-slate-700 text-sm">Standards Breakdown</p>
                  <StandardsBreakdown />
                </div>
              </Card>
            </div>
            
            {/* Right Content Area */}
            <div className="flex flex-col gap-6 flex-1">
              {/* Top Standards Section */}
              <div className="h-[45%]">
                <Card className="w-full px-4 py-6 h-full" shadow="md">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-slate-900">Standards Mapping Status</h3>
                    <p className="text-sm text-slate-600">Current status of ESG standards implementation</p>
                  </div>
                  <StandardsTable />
                </Card>
              </div>
              
              {/* Bottom Reports Section */}
              <div className="flex-1">
                <Card className="w-full px-4 py-4 h-full" shadow="md">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-slate-900">Recent Reports</h3>
                    <p className="text-sm text-slate-600">Latest ESG reports and their status</p>
                  </div>
                  {isLoading ? (
                    <CardSkeletonContainer width="100%" />
                  ) : (
                    <ReportsTable reports={reports} />
                  )}
                </Card>
              </div>
            </div>
          </div>
        </div>
      </BreadCrumbsPage>

      {/* AI Report Generation Modal */}
      <AIReportGenerationModal 
        isOpen={isOpen} 
        onOpenChange={onOpenChange} 
      />
    </div>
  );
};

export default Report;
